/*
 * br-tabs.js — elemento <br-tabs>: abas da Vitrine por categoria
 * (sections/br-category-tabs.liquid). Clique e setas/Home/End trocam a aba;
 * aria-selected, tabindex e hidden acompanham.
 *
 * Editor de temas: clicar numa "Aba" na barra lateral abre essa aba na prévia
 * (evento shopify:block:select; o botão da aba leva o block.shopify_attributes).
 * A aba escolhida fica lembrada enquanto o bloco estiver selecionado, para
 * continuar aberta quando o editor recarrega a seção depois de uma mudança de
 * ajuste. Ao desmarcar o bloco (shopify:block:deselect) a lembrança é apagada e
 * a seção volta a abrir na primeira aba no próximo recarregamento. Esses eventos
 * só existem dentro do editor: na loja o comportamento não muda.
 *
 * Carregado pela própria seção como módulo (type="module"): o navegador executa
 * o módulo uma vez por página para a mesma URL, mesmo com a seção repetida. A
 * trava customElements.get cobre o caso de a URL mudar na mesma página (a versão
 * ?v= muda quando o arquivo é editado e o editor recarrega a seção): sem ela, o
 * segundo define daria erro. Sem dependências; sem o script, a primeira aba fica
 * aberta.
 */

/**
 * Aba selecionada no editor, por seção: id do wrapper .shopify-section →
 * valor do atributo data-shopify-editor-block do botão da aba.
 * @type {Map<string, string>}
 */
const abaDoEditor = new Map();

/**
 * @param {Element} elemento
 * @returns {string}
 */
function chaveDaSecao(elemento) {
  return elemento.closest('.shopify-section')?.id ?? '';
}

class BrTabs extends HTMLElement {
  connectedCallback() {
    this.buttons = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    if (!this.buttons.length) return;

    this.onClick = (event) => {
      const button = event.currentTarget;
      this.select(this.buttons.indexOf(button));
    };

    this.onKeydown = (event) => {
      const current = this.buttons.indexOf(event.currentTarget);
      let target = null;
      if (event.key === 'ArrowRight') target = (current + 1) % this.buttons.length;
      if (event.key === 'ArrowLeft') target = (current - 1 + this.buttons.length) % this.buttons.length;
      if (event.key === 'Home') target = 0;
      if (event.key === 'End') target = this.buttons.length - 1;
      if (target === null) return;
      event.preventDefault();
      this.select(target);
      this.buttons[target].focus();
    };

    this.onBlockSelect = (event) => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('[role="tab"]');
      if (!button || !this.contains(button)) return;
      const index = this.buttons.indexOf(button);
      if (index === -1) return;
      const bloco = button.getAttribute('data-shopify-editor-block');
      if (bloco) abaDoEditor.set(chaveDaSecao(this), bloco);
      this.select(index);
    };

    this.onBlockDeselect = (event) => {
      if (!(event.target instanceof Element) || !this.contains(event.target)) return;
      abaDoEditor.delete(chaveDaSecao(this));
    };

    this.buttons.forEach((button) => {
      button.addEventListener('click', this.onClick);
      button.addEventListener('keydown', this.onKeydown);
    });
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

    // Seção recarregada pelo editor com uma aba selecionada na barra lateral.
    const lembrada = abaDoEditor.get(chaveDaSecao(this));
    if (lembrada) {
      const index = this.buttons.findIndex((button) => button.getAttribute('data-shopify-editor-block') === lembrada);
      if (index > 0) this.select(index);
    }
  }

  disconnectedCallback() {
    if (!this.buttons) return;
    this.buttons.forEach((button) => {
      button.removeEventListener('click', this.onClick);
      button.removeEventListener('keydown', this.onKeydown);
    });
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  select(index) {
    this.buttons.forEach((button, i) => {
      button.setAttribute('aria-selected', i === index ? 'true' : 'false');
      button.setAttribute('tabindex', i === index ? '0' : '-1');
    });
    this.panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });
  }
}

if (!customElements.get('br-tabs')) customElements.define('br-tabs', BrTabs);
